import { useMemo, useState } from "react";
import { Dropdown } from "antd";
import { IconLineCheck, IconLineDown, IconLineUp } from "@kalodata/kds-design-icons";
import { twMerge } from "tailwind-merge";
import "./ClipDropdown.scss";

export interface ClipDropdownOption {
  key: string;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface ClipDropdownProps {
  value?: string;
  onChange: (key: string) => void;
  options: ClipDropdownOption[];
  /** 传了就固定显示，不反映选中项 */
  title?: React.ReactNode;
  /** 未选中且无 title 时回退显示 */
  placeholder?: React.ReactNode;
  disabled?: boolean;
  /** 作用在 trigger button 上 */
  className?: string;
  /** 作用在 popup 内容卡片上（非 antd 定位根节点，避免被 inline min-width 覆盖） */
  overlayClassName?: string;
  placement?: "bottomLeft" | "bottomRight" | "topLeft" | "topRight";
  trigger?: ("click" | "hover")[];
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  /** 自定义触发器节点；传了则不渲染默认 button + 下拉箭头 */
  triggerNode?: React.ReactNode;
  /** 浮层顶部分组标题；传了则在 menu 上方插入一个 12px subtle 标题行 */
  header?: React.ReactNode;
}

export const ClipDropdown = ({
  value,
  onChange,
  options,
  title,
  placeholder,
  disabled,
  className,
  overlayClassName,
  placement = "bottomLeft",
  trigger = ["hover"],
  getPopupContainer,
  triggerNode,
  header,
}: ClipDropdownProps) => {
  const [open, setOpen] = useState(false);

  const selectedOption = useMemo(
    () => (value !== undefined ? options.find((option) => option.key === value) : undefined),
    [options, value],
  );

  const items = useMemo(() => {
    return options.map((option) => {
      const isSelected = selectedOption?.key === option.key;
      return {
        key: option.key,
        disabled: option.disabled,
        label: (
          <div
            className={twMerge(
              "flex h-[32px] items-center justify-between gap-[6px] rounded-[8px] px-[8px] py-[6px] text-[14px] leading-[20px] text-kds-fg hover:bg-kds-bg-overlay cursor-pointer",
              isSelected ? "bg-kds-accent-soft text-kds-accent-fg hover:bg-kds-accent-soft" : "",
            )}
          >
            {option.label}
            {isSelected && <IconLineCheck size={20} className="shrink-0 text-kds-accent-fg" />}
          </div>
        ),
      };
    });
  }, [options, selectedOption]);

  const triggerContent = title ?? selectedOption?.label ?? placeholder;
  const ArrowIcon = open ? IconLineUp : IconLineDown;

  return (
    <Dropdown
      open={disabled ? false : open}
      onOpenChange={setOpen}
      disabled={disabled}
      placement={placement}
      trigger={trigger}
      classNames={{
        root: "clip-dropdown-overlay",
      }}
      getPopupContainer={getPopupContainer}
      menu={{
        items,
        onClick: ({ key }) => onChange(key),
      }}
      popupRender={(menu) => (
        <div
          className={twMerge(
            "rounded-[12px] border border-kds-border bg-kds-bg-card p-[6px] shadow-[0_0_4px_var(--kds-shadow),0_8px_16px_var(--kds-shadow),0_16px_32px_var(--kds-shadow-soft)]",
            overlayClassName,
          )}
        >
          {header && <div className="px-[8px] py-[6px] text-[12px] leading-[20px] text-kds-fg-subtle">{header}</div>}
          {menu}
        </div>
      )}
    >
      {triggerNode ?? (
        <button
          type="button"
          disabled={disabled}
          className={twMerge(
            "flex h-[32px] items-center gap-[6px] rounded-[8px] px-[12px] text-[14px] font-medium leading-[16px] text-kds-fg hover:bg-kds-bg-element cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
            className,
          )}
        >
          <div className="flex-1 flex min-w-0 items-center gap-[6px] truncate">{triggerContent}</div>
          <ArrowIcon size={20} className="shrink-0 text-kds-fg-soft" />
        </button>
      )}
    </Dropdown>
  );
};
